import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLang } from "../../contexts/LanguageContext";
import apiUrls from '../../api/apiUrls';
import { Row, Col } from 'react-bootstrap';
import SettingsOffcanvas from './ChapterSettingsOffcanvas';
import { useAmbientAudio } from '../../hooks/useAmbientAudio';
import { ambientAudioService } from '../../services/AmbientAudioService';
import './ChapterMenu.css'

const ChapterMenu = ({
  bookId,
  chapterId,
  bookLanguage,
  chapterName,
  bookShortTitle,
  bookPrintDate,
  previousChapterId,
  nextChapterId,
  previousChapterHash,
  nextChapterHash,
  preferences,
  onProgress,
  onWordChange,
  wordTimings,
  onFontSizeChange,
  onShowLemmaChange,
  onDictLanguageChange,
  onLastWordReached,
  autoPlay,
  onAutoPlayChange,
  onDialectChange,
}) => {
  const { t } = useLang();

  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);

  // Integración con Audio Ambiental
  const { currentTrackId, playAmbient, pauseAmbient } = useAmbientAudio(preferences);

  const getSeekDataFromClientX = useCallback(
    (clientX) => {
      if (!progressRef.current || !duration) return null;
      const rect = progressRef.current.getBoundingClientRect();
      const offsetX = Math.min(Math.max(clientX - rect.left, 0), rect.width);
      const ratio = rect.width ? offsetX / rect.width : 0;
      return {
        ratio,
        time: ratio * duration,
      };
    },
    [duration]
  );

  const updateDragPreviewFromClientX = useCallback(
    (clientX) => {
      if (typeof clientX !== 'number') return null;
      const data = getSeekDataFromClientX(clientX);
      if (!data) return null;
      setDragPreview(data);
      return data;
    },
    [getSeekDataFromClientX]
  );

  const commitSeek = useCallback(
    (data) => {
      if (!data) return;
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = data.time;
      setCurrentTime(data.time);
      setDragPreview(null);
    },
    []
  );

  useEffect(() => {
    const handleWindowMouseMove = (event) => {
      if (!isDragging) return;
      updateDragPreviewFromClientX(event.clientX);
    };

    const handleWindowMouseUp = (event) => {
      if (!isDragging) return;
      const data = dragPreview ?? getSeekDataFromClientX(event.clientX);
      commitSeek(data);
      setIsDragging(false);
    };

    const handleWindowTouchMove = (event) => {
      if (!isDragging) return;
      const touch = event.touches && event.touches[0];
      if (!touch) return;
      updateDragPreviewFromClientX(touch.clientX);
    };

    const handleWindowTouchEnd = (event) => {
      if (!isDragging) return;
      const touch = event.changedTouches && event.changedTouches[0];
      const data = dragPreview ?? (touch ? getSeekDataFromClientX(touch.clientX) : null);
      commitSeek(data);
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('touchmove', handleWindowTouchMove, { passive: true });
    window.addEventListener('touchend', handleWindowTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('touchmove', handleWindowTouchMove);
      window.removeEventListener('touchend', handleWindowTouchEnd);
    };
  }, [isDragging, dragPreview, updateDragPreviewFromClientX, commitSeek, getSeekDataFromClientX]);

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
      onProgress?.(audio.currentTime);

      if (onWordChange && wordTimings && wordTimings.length > 0) {
        // Encontrar la palabra actual basada en el tiempo del audio
        // Buscamos la última palabra cuyo tiempo de inicio sea menor o igual al actual
        const activeWord = [...wordTimings]
          .reverse()
          .find((word) => audio.currentTime >= word.start);

        if (activeWord) {
          onWordChange(activeWord.id);
          
          // Lógica para detectar el final del capítulo
          // Comparamos el ID de la palabra activa con el ID de la última palabra del array
          const lastWord = wordTimings[wordTimings.length - 1];
          if (activeWord.id === lastWord.id && onLastWordReached && autoPlay) {
            // Verificamos si realmente estamos cerca del final del tiempo de esa palabra
            if (audio.currentTime >= activeWord.end - 0.1) {
              onLastWordReached();
            }
          }
        }
      }
    };

    const handleLoadedMetadata = () => {
      const audioEl = audioRef.current;
      setDuration(audioEl.duration);

      if (autoPlay) {
        audioEl
          .play()
          .then(() => {
            const playPauseButton = document.getElementById('playPauseButton');
            if (playPauseButton) {
              playPauseButton.classList.add('pause');
              playPauseButton.classList.remove('play');
            }
            // Sincronizar con audio ambiental
            if (currentTrackId) {
              playAmbient();
            }
          })
          .catch((error) => {
            console.error('Error al intentar reproducir el audio automáticamente:', error);
          });
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [autoPlay, isDragging, onLastWordReached, onProgress, onWordChange, wordTimings, currentTrackId, playAmbient]);

  useEffect(() => {
    const audio = audioRef.current;
    const speedValues = [0.5, 0.75, 1, 1.25, 1.5];
    if (preferences && preferences.text_speed !== undefined) {
      audio.playbackRate = speedValues[preferences.text_speed];
    }
  }, [preferences]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play();
      document.getElementById('playPauseButton')?.classList.add('pause');
      document.getElementById('playPauseButton')?.classList.remove('play');
      
      // Sincronizar con audio ambiental
      if (currentTrackId) {
        playAmbient();
      }
    } else {
      audio.pause();
      document.getElementById('playPauseButton')?.classList.add('play');
      document.getElementById('playPauseButton')?.classList.remove('pause');
      
      // Sincronizar con audio ambiental
      pauseAmbient();
    }
  };

  const handlePrevChapter = () => {
    if (previousChapterHash) {
      window.location.href = `/chapter?id=${previousChapterHash}`;
    } else if (previousChapterId) {
      window.location.href = `/chapter?book_id=${bookId}&chapter_id=${previousChapterId}`;
    }
  };

  const handleNextChapter = () => {
    if (nextChapterHash) {
      window.location.href = `/chapter?id=${nextChapterHash}`;
    } else if (nextChapterId) {
      window.location.href = `/chapter?book_id=${bookId}&chapter_id=${nextChapterId}`;
    }
  };

  const handleProgressClick = (event) => {
    if (isDragging) return;
    const data = getSeekDataFromClientX(event.clientX);
    commitSeek(data);
  };

  const handleMouseDown = (event) => {
    const data = updateDragPreviewFromClientX(event.clientX);
    if (data) {
      setIsDragging(true);
    }
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    updateDragPreviewFromClientX(event.clientX);
  };

  const handleMouseUp = () => {};

  const handleTouchStart = (event) => {
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    const data = updateDragPreviewFromClientX(touch.clientX);
    if (data) {
      setIsDragging(true);
    }
  };

  const handleTouchMove = (event) => {
    if (!isDragging) return;
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    updateDragPreviewFromClientX(touch.clientX);
  };

  const handleTouchEnd = () => {};

  const [show, setShowOffcanvas] = useState(false);
  const handleCloseOffcanvas = () => setShowOffcanvas(false);
  const handleShowOffcanvas = () => setShowOffcanvas(true);

  const book_title_info = `${bookShortTitle} - ${bookPrintDate} (${chapterName})`;

  const formattedDialect = preferences.dialect_code?.replace('-', '_') || 'es_ES';
  const audioUrl = `${apiUrls.bookFiles}${bookId}/${bookId}_${chapterId}_${formattedDialect}.mp3`;

  const progressRatio = duration ? currentTime / duration : 0;
  const previewRatio = dragPreview ? dragPreview.ratio : null;
  const handleRatio = previewRatio ?? progressRatio;
  const overlayLeft = previewRatio !== null ? Math.min(progressRatio, previewRatio) : 0;
  const overlayWidth = previewRatio !== null ? Math.abs(previewRatio - progressRatio) : 0;

  const handleDialectChange = (newDialect) => {
    const audio = audioRef.current;
    const newAudioUrl = `${apiUrls.bookFiles}${bookId}/${bookId}_${chapterId}_${newDialect}.mp3`;
    audio.src = newAudioUrl;

    onDialectChange(newDialect);

    audio.addEventListener(
      'canplaythrough',
      () => {
        audio
          .play()
          .then(() => {
            const playPauseButton = document.getElementById('playPauseButton');
            if (playPauseButton) {
              playPauseButton.classList.add('pause');
              playPauseButton.classList.remove('play');
            }
            // Sincronizar con audio ambiental
            if (currentTrackId) {
              playAmbient();
            }
          })
          .catch((error) => {
            console.error(t("CHAPTER_ERROR_AUTOPLAY_AUDIO"), error);
          });
      },
      { once: true }
    );
  };

  const handleSpeedChange = (newSpeed) => {
    const audio = audioRef.current;
    const speedValues = [0.5, 0.75, 1, 1.25, 1.5];
    audio.playbackRate = speedValues[newSpeed];
  };

  return (
    <nav className="navbar fixed-top navbar-light bg-light reader_navbar" id="header_nav">
      <div className="info_book">
        <div className="container-sm">
          <audio ref={audioRef} src={audioUrl} controls style={{ display: 'none' }} />
          <Row className="g-0">
            <Col xs={12} id="book_title_sm">
              <div >{book_title_info}</div>
            </Col>
            <Col xs={12}>
              <div className="chapter_menu">
                <div className="chapter_menu_left">
                  <a href={`/book?book_id=${bookId}`} id="backLink" className="backLink" title={t("CHAPTER_BACK")}></a>
                  <button id="playPauseButton" title={t("CHAPTER_PLAY")} className="playPauseButton" onClick={handlePlayPause}></button>

                  {previousChapterId && (
                    <a href="#" id="prevLink" className="prevLink" title={t("CHAPTER_PREV")} onClick={handlePrevChapter}></a>
                  )}
                  {nextChapterId && (
                    <a href="#" id="nextLink" className="nextLink" title={t("CHAPTER_NEXT")} onClick={handleNextChapter}></a>
                  )}
                </div>

                <div className="chapter_menu_center">
                  <div id="book_title_lg">{book_title_info}</div>
                </div>

                <div className="chapter_menu_right">
                  <button onClick={handleShowOffcanvas} className="settings_button" type="button" title={t("CHAPTER_SETTINGS_TITLE")}></button>
                </div>
              </div>
            
            </Col>
            <Col xs={12} className='chapter-menu-row3'>
            
              <div id="chapter_time">
                <span id="currentTime">{new Date(currentTime * 1000).toISOString().substr(11, 8)}</span>{' '}/{' '}
                <span id="duration">{new Date(duration * 1000).toISOString().substr(11, 8)}</span>
              </div>  
            
            </Col>
          </Row> 
          
        </div>

        <div id="progressBar">
          
          <div
            id="progress"
            ref={progressRef}
            onClick={handleProgressClick}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="progress-filled"
              style={{ width: `${progressRatio * 100}%` }}
            ></div>
            {previewRatio !== null && overlayWidth > 0 && (
              <div
                className="progress-preview"
                style={{
                  left: `${overlayLeft * 100}%`,
                  width: `${overlayWidth * 100}%`,
                }}
              ></div>
            )}
            <div
              className="progress-handle"
              style={{ left: `${handleRatio * 100}%` }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleMouseDown(e);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                handleTouchStart(e);
              }}
            >
              <span className="progress-handle-dot" />
            </div>
          </div>
        </div>
      </div>
      <SettingsOffcanvas
        show={show}
        handleClose={handleCloseOffcanvas}
        preferences={preferences}
        bookLanguage={bookLanguage}
        onDialectChange={onDialectChange}
        onFontSizeChange={onFontSizeChange}
        onShowLemmaChange={onShowLemmaChange}
        onDictLanguageChange={onDictLanguageChange}
        onSpeedChange={handleSpeedChange}
        onAutoPlayChange={onAutoPlayChange}
      />
    </nav>
  );
};

export default ChapterMenu;
